// THE MATTER IN MOTION: the harvesting looks that carry the commodity from
// the field into the hold (R1120, mockup round 055 group b).
//
// One entry per look id of `labels.toml gather_styles` that this file owns:
// `chips-lift` (card 06), `drag-loop` (card 07) and `grab-beats` (card 08).
// `gather-styles.ts` prefers this map over the AUTO look, so a look that is
// not here yet draws the AUTO look and the map fills in one card at a time.
//
// **PENDING names every card of this file that is not built yet.** An id must
// stand in `RECIPES` or in `PENDING`, and `gather-recipes-matter.test.ts`
// fails when one stands in neither. An agent that builds a card moves its id
// out of `PENDING`, adds its row to `data/presentation/gather.toml
// [[gather.look]]`, and touches no other file.
//
// **A recipe reads its figures out of the row and holds no literal.** The row
// carries the fourteen figure fields the card's own TOML comment maps. A
// PROPORTION is not a figure: which quadrant of a loop runs inbound, how far
// a rim sits outside its fill, how a fade is shaped — that stays here as a
// module constant, the way `FLASH_CORE_FLOOR` does in the combat kit.
//
// This is a leaf (`leaves.test.ts`): it imports the kit and the generated
// pack, and never an engine.

import {
  DIM_INK, GATHER_CHIP_HALF_Y, PALE_INK, WATER_INK, box, chip, chipOutline,
  TAU, clamp01, diamond, ease, ellipse, frac, lerp, rect, stayEnv, wedge,
  type GatherCtx, type GatherRecipe, type Pt,
} from './gather-recipe-kit';

// ---------------------------------------------------------------------------
// The proportions
// ---------------------------------------------------------------------------
//
// A SIZE the look invents is a figure and stands in the row. A scale OF A
// MARK THE MAP ALREADY DRAWS — the deposit diamond of card 08 — is a
// proportion and stands here, because the mark states its own size.

/** drag-loop, card 07: how many segments the loop is walked as. The card
 *  draws one `ellipse` path; the circle sink draws circles only, so the loop
 *  is a polyline and this is the count that reads round at every zoom. */
const LOOP_STEPS = 32;

/** drag-loop: the dash of the SHAFT loop, in world px. The card writes
 *  `[1.6, 2]`, and `dashOn`/`dashOff` take the two figures straight. */
const LOOP_SHAFT_DASH_ON = 1.6, LOOP_SHAFT_DASH_OFF = 2;

/**
 * drag-loop: which part of the loop runs INBOUND.
 *
 * The card reads `floor(th / (PI/2)) % 2 === 0`, which is quadrants 0 and 2 of
 * the loop: a bucket is full over the half of the turn that carries it toward
 * the hull and hollow over the half that carries it back.
 */
const LOOP_QUADRANT = Math.PI / 2;

/**
 * chips-lift, card 06: the bearing a chip leaves the rim of the field on,
 * measured from the hull's heading, and the two extra bearings a drain step
 * sends a chip on.
 *
 * A bearing says WHERE on the rim the path starts, the way `LOOP_QUADRANT`
 * says which part of the loop runs inbound, so it is the shape of the lift
 * and not a figure of it. The row carries the two radii the path runs
 * between and the radians it swings over.
 */
const CHIP_LAUNCH = 2.09;
const CHIP_STEP_BEARINGS: readonly number[] = [1, 1.75, -1, 1.75, 1, 2.45];

/** chips-lift: the share of the flight the chip spends on its way OUT to the
 *  swing radius. It curls in to the hull over the rest. */
const CHIP_OUT_SHARE = 0.3;

/** chips-lift: how far a chip shrinks over its flight, as a share of its own
 *  launch size, and how much smaller each chip of its trail stands. */
const CHIP_TAPER = 0.65;
const CHIP_TRAIL_STEP = 0.22;

/** chips-lift: how many chips the trail carries behind the head, and how far
 *  back along the path each one lags. */
const CHIP_TRAIL = 3;
const CHIP_TRAIL_LAG = 0.04;

/** chips-lift: the share of the flight a chip fades in over and out over. */
const CHIP_FADE_IN = 0.08;
const CHIP_FADE_OUT = 0.1;

/** chips-lift: how far back along its path a chip falls while the full-hold
 *  beat runs, as a share of the flight. */
const CHIP_FALL_BACK = 0.15;

/** chips-lift: a hair of slack on the test that asks whether the cut has
 *  sent a chip yet, so a rounding hair does not drop one. */
const CHIP_SENT_SLACK = 0.001;

/** chips-lift: where on its path a drain-step chip springs from, and how far
 *  it shrinks over that spring, as a share of its own size. */
const CHIP_STEP_START = 0.12;
const CHIP_STEP_SHRINK = 0.6 / 1.15;

/** chips-lift: how hard a drain step snaps its chips to full ink. */
const CHIP_STEP_SNAP = 4;

/** chips-lift: the scale of the `chip` cell that draws a chip of one world
 *  px in height. The row states a chip's HEIGHT, because a length is the
 *  one unit a figure field carries (R1052). */
const CHIP_PER_PX = 1 / (2 * GATHER_CHIP_HALF_Y);

/** grab-beats, card 08: the eight sectors the jaws stand on and the pitch of
 *  one. Four jaws take every second sector from sector 1, and the two jaws
 *  of a shaft take sectors 1 and 5, so no jaw stands on the bow or astern. */
const JAW_SECTORS = 8;
const JAW_PITCH = Math.PI / 4;

/** grab-beats: the share of a lot the snap takes, and the share of a lot the
 *  flash of the field lives over. Both scale with the rate, so a shaft snaps
 *  one time in each of its own slow lots. */
const JAW_SHUT_SHARE = 0.12;
const JAW_FLASH_SHARE = 0.25;

/** grab-beats: how hard the full hold throws the jaws open, and how hard it
 *  snaps them to the cargo brown. */
const JAW_OPEN_SNAP = 2.5;
const JAW_CARGO_SNAP = 1.5;

/** grab-beats: how far outside the deposit diamond the flash stands, and how
 *  far it springs out beyond that, as shares of the mark's own size. The
 *  mark states its size, so this is a proportion. */
const JAW_FLASH_SCALE = 1.15;
const JAW_FLASH_SPRING = 0.3;

/** The reused scratch of this file (R131). */
const LOOP_X = new Float32Array(LOOP_STEPS + 1);
const LOOP_Y = new Float32Array(LOOP_STEPS + 1);
const P0: Pt = { x: 0, y: 0 };

// ---------------------------------------------------------------------------
// The paths
// ---------------------------------------------------------------------------

/**
 * chips-lift: where one chip stands at `u` of its flight, on `side` of the
 * hull's heading and launched on the bearing `a0`.
 *
 * It is `bChipAt` of the card. The chip leaves the rim of the field abeam,
 * swings out to the swing radius toward the bow quarter and curls in to the
 * centre of the hull, and it never enters the sector astern where the ten
 * Dredger marks live (R835). The card draws with the FIELD at the origin and
 * walks toward `sc.D`, so the recipe walks from the field toward the hull.
 */
function chipAt(c: GatherCtx, u: number, side: number, a0: number, out: Pt): Pt {
  const r = c.row;
  const rad = u < CHIP_OUT_SHARE
    ? lerp(r.r2Px, r.rPx, ease(u / CHIP_OUT_SHARE))
    : r.rPx * (1 - ease((u - CHIP_OUT_SHARE) / (1 - CHIP_OUT_SHARE)));
  const ang = c.restHead + side * (a0 - r.speed * ease(u));
  out.x = c.fx + (c.hx - c.fx) * u + Math.cos(ang) * rad;
  out.y = c.fy + (c.hy - c.fy) * u + Math.sin(ang) * rad;
  return out;
}

/** chips-lift: the fill alpha of chip `q` of a trail, the head first. */
function trailAlpha(c: GatherCtx, q: number): number {
  const r = c.row;
  return q === 0 ? r.alpha : q === 1 ? r.alpha2 : r.alpha3;
}

// ---------------------------------------------------------------------------
// The recipes
// ---------------------------------------------------------------------------

export const RECIPES: Readonly<Record<string, GatherRecipe>> = {
  /**
   * Card 06, CHIPS LIFT. The AUTO look (R1279).
   *
   * Small chips in the shape of the satellite chips of the deposit leave the
   * rim of the field, swing out to the swing radius and curl in to the
   * centre of the hull. One chip arrives with each lot, and a drain step
   * sends three more. The shaft lifts one hollow chip at a time. At a full
   * hold the chips in flight turn hollow in the cargo brown and fall back.
   */
  'chips-lift': {
    mid(c: GatherCtx): void {
      const r = c.row;
      const n = c.shaft ? r.count2 : r.count;
      if (n < 1) return;
      const lots = c.lotN + c.lot;
      const head = r.lengthPx * CHIP_PER_PX;
      const big = r.length2Px * CHIP_PER_PX;
      if (c.full > 0) {
        // The hold is full: every chip in flight turns hollow, takes the
        // cargo brown and falls back along its own path.
        for (let i = 0; i < n; i++) {
          const g = Math.floor((lots + i) / n);
          const u = frac((lots + i) / n) - (1 - c.full) * CHIP_FALL_BACK;
          if (u <= 0) continue;
          chipAt(c, u, (g + i) % 2 ? 1 : -1, CHIP_LAUNCH, P0);
          chipOutline(c, P0.x, P0.y, head, r.widthPx, c.cargo, r.alpha4 * c.full);
        }
        return;
      }
      if (!c.atRest || c.work <= 0) return;
      for (let i = 0; i < n; i++) {
        const g = Math.floor((lots + i) / n);
        const u = frac((lots + i) / n);
        const side = (g + i) % 2 ? 1 : -1;
        // The cut has not sent this chip yet.
        if (lots - u * n < -CHIP_SENT_SLACK) continue;
        const al = clamp01(u / CHIP_FADE_IN) * clamp01((1 - u) / CHIP_FADE_OUT) * c.work;
        const k = lerp(1, CHIP_TAPER, ease(u));
        if (c.shaft) {
          // The shaft lifts ONE chip and lifts it hollow, so the two states
          // read apart by shape and by count and not by colour (R715).
          chipAt(c, u, side, CHIP_LAUNCH, P0);
          chipOutline(c, P0.x, P0.y, big, r.widthPx, c.res, r.alpha4 * al);
          continue;
        }
        for (let q = CHIP_TRAIL - 1; q >= 0; q--) {
          const uq = u - q * CHIP_TRAIL_LAG;
          if (uq < 0) continue;
          chipAt(c, uq, side, CHIP_LAUNCH, P0);
          chip(c, P0.x, P0.y, head * k * (1 - q * CHIP_TRAIL_STEP), c.res,
            al * trailAlpha(c, q));
        }
      }
      if (c.step > 0 && !c.shaft) {
        // A drain step sends three more chips, which spring out together on
        // their own bearings and shrink as they go.
        const v = CHIP_STEP_START + (1 - CHIP_STEP_START) * (1 - c.step);
        const al = r.alpha4 * clamp01(c.step * CHIP_STEP_SNAP) * c.work;
        for (let b = 0; b < CHIP_STEP_BEARINGS.length; b += 2) {
          chipAt(c, v, CHIP_STEP_BEARINGS[b], CHIP_STEP_BEARINGS[b + 1], P0);
          chip(c, P0.x, P0.y, lerp(big, big * CHIP_STEP_SHRINK, v), c.resHi, al);
        }
      }
    },
  },

  /**
   * Card 07, DRAG LOOP.
   *
   * A bucket chain runs on a thin loop ACROSS the hull, over the field. A
   * bucket is full on its way to the hull and hollow on its way out, and one
   * bucket passes each end of the loop with each lot. The shaft chain runs at
   * a quarter of the pace, its loop is dashed, and every `count2`-th bucket
   * comes back hollow. At a full hold the chain stops and every bucket stays
   * full, in the cargo brown.
   */
  'drag-loop': {
    mid(c: GatherCtx): void {
      const env = stayEnv(c);
      if (env <= 0) return;
      const r = c.row;
      const n = r.count;
      if (n < 1) return;
      const lots = c.lotN + c.lot;
      // The chain stops at a full hold. `fill` is −1 on a hull whose hold the
      // viewer cannot read (R1121), and an unread hold is never full.
      const stop = c.fill >= 1;
      // The loop wears the dim water while the cut runs, and the cargo brown
      // once the hold is full. Neither is a look and neither is a theme, so
      // both are inks and not row figures.
      const loopInk = stop ? c.cargo : DIM_INK;
      ellipse(
        c, c.fx, c.fy, r.rPx, r.r2Px, c.restHead, LOOP_STEPS, LOOP_X, LOOP_Y,
        r.widthPx, loopInk, r.alpha2 * env,
        c.shaft ? LOOP_SHAFT_DASH_ON : 0, c.shaft ? LOOP_SHAFT_DASH_OFF : 0,
      );
      const cos = Math.cos(c.restHead), sin = Math.sin(c.restHead);
      // The rim box of a full bucket is its side plus its own rim stroke, so
      // the rim stands just outside the fill, as the card's two rects do.
      const rimSide = r.lengthPx + r.width2Px;
      for (let i = 0; i < n; i++) {
        const th = TAU * frac((i + lots) / n);
        const inbound = Math.floor(th / LOOP_QUADRANT) % 2 === 0;
        // The loop's own frame: the half beam across the hull, the half
        // length along the chain.
        const bx = r.rPx * Math.sin(th), by = r.r2Px * Math.cos(th);
        const px = c.fx + cos * bx - sin * by;
        const py = c.fy + sin * bx + cos * by;
        const laden = stop || (inbound && !(c.shaft && r.count2 > 0 && i % r.count2 !== 0));
        if (laden) {
          rect(c, px, py, r.lengthPx, r.lengthPx, c.restHead,
            stop ? c.cargo : c.res, r.alpha * env);
          box(c, px, py, rimSide, rimSide, c.restHead, r.width2Px,
            WATER_INK, r.alpha3 * env);
        } else {
          rect(c, px, py, r.length2Px, r.length2Px, c.restHead,
            WATER_INK, r.alpha3 * env);
          box(c, px, py, r.length2Px, r.length2Px, c.restHead, r.width2Px,
            c.res, r.alpha4 * env);
        }
      }
    },
  },

  /**
   * Card 08, GRAB BEATS.
   *
   * Four jaws stand at 45 degrees to the hull, clear of the bow and of the
   * mark. They snap shut onto the field at the start of each lot, the field
   * flashes, and the jaws open slowly through the rest of the lot. The shaft
   * has two jaws only, and they close one time in each slow lot. At a full
   * hold the jaws open, turn to the cargo brown and fade.
   */
  'grab-beats': {
    mid(c: GatherCtx): void {
      const env = stayEnv(c);
      if (env <= 0 || c.leaving > 0) return;
      const r = c.row;
      const n = c.shaft ? r.count2 : r.count;
      if (n < 1) return;
      const open = r.rPx, shut = r.r2Px;
      // The snap takes the same share of a lot at either rate, so a shaft
      // closes one time in each of its own slow lots.
      const snap = JAW_SHUT_SHARE * c.rate;
      const lot = c.lot;
      const pull = lot / snap;
      let rad = lot < snap
        ? lerp(open, shut, pull * pull)
        : lerp(shut, open, ease((lot - snap) / (1 - snap)));
      let al = env * lerp(r.alpha, r.alpha2, clamp01((rad - shut) / (open - shut)));
      let ink = PALE_INK;
      // `fill` is −1 on a hull whose hold the viewer cannot read (R1121),
      // and an unread hold is never full.
      if (c.fill >= 1) {
        rad = lerp(rad, open, clamp01((1 - c.full) * JAW_OPEN_SNAP));
        al = clamp01(c.full * JAW_CARGO_SNAP) * r.alpha4;
        ink = c.cargo;
      } else if (lot >= snap) {
        const fl = clamp01(1 - (lot - snap) / (JAW_FLASH_SHARE * c.rate)) * env;
        if (fl > 0) {
          diamond(c, c.fx, c.fy, JAW_FLASH_SCALE + JAW_FLASH_SPRING * (1 - fl),
            r.widthPx, c.resHi, r.alpha3 * fl);
        }
      }
      // The jaw is a filled triangle with its apex on the ring and its base
      // outward: the row states the jaw's own edge and its half angle, so
      // the wedge takes the two sides of that triangle.
      const len = r.lengthPx * Math.cos(r.speed);
      const hw = r.lengthPx * Math.sin(r.speed);
      const step = JAW_SECTORS / n;
      for (let i = 0; i < n; i++) {
        const a = c.restHead + (1 + i * step) * JAW_PITCH;
        wedge(c, c.fx + Math.cos(a) * rad, c.fy + Math.sin(a) * rad, a, len, hw, ink, al);
      }
    },
  },
};

/**
 * The cards of this file that wait for their own recipe, in card order.
 *
 * The table is EMPTY: `chips-lift` (card 06), `drag-loop` (card 07) and
 * `grab-beats` (card 08) are all built.
 */
export const PENDING: readonly string[] = [];
